import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  $getSelection,
  $isRangeSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  SELECTION_CHANGE_COMMAND,
} from "lexical";

import { $isLinkNode } from "@lexical/link";
import { $isListNode, ListNode } from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $isHeadingNode } from "@lexical/rich-text";
import { $getNearestNodeOfType, mergeRegister } from "@lexical/utils";

import {
  defaultToolbarState,
  ToolbarContext,
  type ToolbarState,
} from "../toolbar/toolbarContext";

export function ToolbarStatePlugin({ children }: PropsWithChildren) {
  const [editor] = useLexicalComposerContext();
  const [state, setState] = useState<ToolbarState>(defaultToolbarState);

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();

    if (!$isRangeSelection(selection)) return;

    const anchorNode = selection.anchor.getNode();
    const topLevelElement = anchorNode.getTopLevelElement();

    if (!topLevelElement) return;

    let blockType = topLevelElement.getType();

    if ($isListNode(topLevelElement)) {
      const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
      blockType = parentList
        ? parentList.getListType()
        : topLevelElement.getListType();
    } else if ($isHeadingNode(topLevelElement)) {
      blockType = topLevelElement.getTag();
    }

    const parent = anchorNode.getParent();

    setState((prev) => ({
      ...prev,
      isBold: selection.hasFormat("bold"),
      isItalic: selection.hasFormat("italic"),
      isUnderline: selection.hasFormat("underline"),
      isStrikethrough: selection.hasFormat("strikethrough"),
      isCode: selection.hasFormat("code"),
      isSubscript: selection.hasFormat("subscript"),
      isSuperscript: selection.hasFormat("superscript"),
      isLink: $isLinkNode(parent) || $isLinkNode(anchorNode),
      blockType,
    }));
  }, []);

  useEffect(
    () =>
      mergeRegister(
        editor.registerCommand(
          SELECTION_CHANGE_COMMAND,
          () => {
            $updateToolbar();

            return false;
          },
          COMMAND_PRIORITY_CRITICAL,
        ),
        editor.registerCommand(
          CAN_UNDO_COMMAND,
          (payload: boolean) => {
            setState((prev) => ({ ...prev, canUndo: payload }));

            return false;
          },
          COMMAND_PRIORITY_CRITICAL,
        ),
        editor.registerCommand(
          CAN_REDO_COMMAND,
          (payload: boolean) => {
            setState((prev) => ({ ...prev, canRedo: payload }));

            return false;
          },
          COMMAND_PRIORITY_CRITICAL,
        ),
        editor.registerUpdateListener(({ editorState }) => {
          editorState.read(() => {
            $updateToolbar();
          });
        }),
      ),
    [editor, $updateToolbar],
  );

  return (
    <ToolbarContext.Provider value={state}>{children}</ToolbarContext.Provider>
  );
}
